import type { LucideIcon } from "lucide-react";
import {
  Clapperboard,
  CreditCard,
  Crown,
  DollarSign,
  Eye,
  Film,
  FlaskConical,
  Gift,
  Globe2,
  History,
  Play,
  HeartPulse,
  Image,
  Layout,
  LayoutDashboard,
  Layers,
  LineChart,
  MapPin,
  MapPinned,
  Mail,
  MessageCircle,
  Repeat2,
  Scale,
  ServerCog,
  Settings2,
  ShoppingCart,
  SlidersHorizontal,
  Sparkles,
  Tv2,
  TrendingUp,
  Wallet,
  Users,
  Video,
  BarChart3,
  CalendarRange,
  Radio,
  Zap,
} from "lucide-react";

export interface AdminSidebarNavChild {
  readonly to: string;
  readonly label: string;
  readonly icon: LucideIcon;
}

/** Entries shown in AdminLayout — must match nested routes under ProtectedAdminRoute in App.tsx */
export interface AdminSidebarNavItem {
  readonly to: string;
  readonly label: string;
  readonly icon: LucideIcon;
  /** When true, only exact path matches for active state (e.g. /admin vs /admin/…) */
  readonly end: boolean;
  /** Optional one-line description (used in the ⌘K command palette) */
  readonly hint?: string;
  /** Sub-items rendered as an expandable group */
  readonly children?: readonly AdminSidebarNavChild[];
  /** When true, only super admins can see this item */
  readonly superAdminOnly?: boolean;
  /**
   * Métricas de ganancias (brand-isolated). Es lo ÚNICO que ve el rol acotado
   * `{ role: "admin", approved: true }` (revenue-only admin). Ver
   * isPublicMetadataRevenueOnlyAdmin + adminNavVisibility.
   */
  readonly revenueMetrics?: boolean;
  /**
   * Badge de trabajo pendiente (usePendingCounts): el AdminLayout muestra un
   * count ámbar cuando hay pendientes de este tipo.
   */
  readonly pendingKey?: "memberships" | "free";
}

/** A labelled section of the sidebar (2026 grouped IA — improves scanability) */
export interface AdminSidebarNavGroup {
  readonly label: string;
  readonly items: readonly AdminSidebarNavItem[];
}

/**
 * Grouping criterion: each group has a single axis.
 *  - General:        landing del admin
 *  - Mux · Membresía: templates con video Mux + checkout membresía (prioridad producto)
 *  - Contenido:      lo que se ve en el sitio público (sin funnels mux premium)
 *  - Pagos:          dinero — operación diaria (sin config mux duplicada)
 *  - Analítica:      medición — todo lo que va al hub /admin/analytics (+ BA report)
 *  - Geo & Edge:     ruteo por país/ciudad + flags del Worker
 *  - Infraestructura: meta-config del propio admin / plataforma
 */
export const ADMIN_SIDEBAR_NAV_GROUPS: readonly AdminSidebarNavGroup[] = [
  {
    label: "General",
    items: [
      {
        to: "/admin",
        label: "Dashboard",
        icon: LayoutDashboard,
        end: true,
        hint: "Resumen y métricas",
      },
      {
        to: "/admin/site-settings",
        label: "Configuración del sitio",
        icon: Layout,
        end: false,
        // FIX 2026-06: el hint NO debe incluir "hero CTA". El Hero CTA vive
        // en /admin/hero-cta (Geo & Edge → Hero CTA por región). Acá solo
        // texto del template base (header/logo/Sobre mí/galería).
        hint: "Header, logo, Sobre mí, galería · texto del template base",
      },
    ],
  },
  {
    label: "Mux · Membresía",
    items: [
      {
        to: "/admin/dual-cta-variants",
        label: "Mux+CTA · Variantes",
        icon: FlaskConical,
        end: false,
        hint: "Gestioná video Mux + VIP/Free + Stripe/promo por variante. Asigná por país/estado/ciudad (A/B). Save → KV live ~1–2s. Fallback Exclusive $39.99 + EXCLUSIVE75.",
        superAdminOnly: true,
      },
      {
        to: "/admin/preview/dual",
        label: "Preview Mux+CTA",
        icon: Play,
        end: false,
        hint: "Home + /membership con variante forzada — validar mux/labels/promo antes de publicar",
        superAdminOnly: true,
      },
      {
        to: "/admin/dual-cta-visibility",
        label: "Visibilidad VIP / Free",
        icon: SlidersHorizontal,
        end: false,
        hint: "Kill-switch VIP/Free en home video y /ser-miembro · overrides de label · live ~1–2s",
        superAdminOnly: true,
      },
      {
        to: "/admin/stripe-checkout",
        label: "Stripe · checkout",
        icon: ShoppingCart,
        end: false,
        hint: "Copy Mux+CTA (labels globales), imagen + descripción de Stripe Checkout, descuentos",
      },
      {
        to: "/admin/premium-invite-config",
        label: "Invitación premium",
        icon: Clapperboard,
        end: false,
        hint: "Cards bio, embudo, textos y overlay — función separada de Reels",
        children: [
          { to: "/admin/premium-invite-config#cards-bio-premium-simple", label: "Cards Bio", icon: MessageCircle },
          { to: "/admin/premium-invite-config#embudo", label: "Embudo & Texto", icon: Layers },
        ],
      },
      {
        to: "/admin/reels",
        label: "Reels",
        icon: Tv2,
        end: false,
        hint: "Función Reels: layout A/B, playlist Mux, player, preview y analítica",
        children: [
          { to: "/admin/reels", label: "Resumen", icon: Clapperboard },
          { to: "/admin/reels/layout", label: "Layout A/B", icon: Layout },
          { to: "/admin/reels/playback", label: "Clips / playlist", icon: Film },
          { to: "/admin/reels/player", label: "Player", icon: Play },
          { to: "/admin/reels/preview", label: "Preview", icon: Eye },
          { to: "/admin/reels/analytics", label: "Analítica", icon: BarChart3 },
        ],
      },
      {
        to: "/admin/memberships",
        label: "Aprobar membresías",
        icon: Crown,
        end: false,
        hint: "Cola post-pago: acceso Telegram y planes pendientes",
        pendingKey: "memberships",
      },
      {
        to: "/admin/users",
        label: "Usuarios",
        icon: Users,
        end: false,
        hint: "Todos los usuarios Clerk con su membresía · banear/desbanear (sin emails)",
      },
      {
        to: "/admin/free-members",
        label: "Miembros Free",
        icon: Users,
        end: false,
        hint: "Pendientes de aprobación del tier free — aprobar envía el link de Telegram por email",
        superAdminOnly: true,
        pendingKey: "free",
      },
      {
        to: "/dashboard/subscripciones/planes",
        label: "Planes Stripe/MP por nivel",
        icon: Repeat2,
        end: false,
        hint: "stripe_price_id + mercadopago_plan_id por nivel (basic / standard / vip / platinum)",
      },
      {
        to: "/admin/mercadopago-plans",
        label: "Planes MercadoPago",
        icon: CreditCard,
        end: false,
        hint: "Suscripciones y pagos únicos MP · funnel AR /ser-miembro",
      },
      {
        to: "/admin/hero-cta",
        label: "Hero CTA · región",
        icon: Gift,
        end: false,
        hint: "Tipo dual en AR → /ser-miembro (Mux variante). Intl solo si template hero-compatible",
      },
      {
        to: "/admin/country-template",
        label: "Template por país",
        icon: Globe2,
        end: false,
        hint: "Capa 1 intl: template en / (default USExclusiveLanding si no hay override). Save → country-template-config → KV. Mux/CTA: dual-cta-variants",
        superAdminOnly: true,
      },
      {
        to: "/admin/analytics?tab=mux",
        label: "Analítica de video",
        icon: Film,
        end: false,
        hint: "Vistas Mux por asset · cruzar con cambios de variante",
        superAdminOnly: true,
      },
      {
        to: "/dashboard/dual-cta-funnel",
        label: "Funnel por variante",
        icon: BarChart3,
        end: false,
        hint: "Impresiones → plays → VIP clicks → checkout → conversiones por variante A/B",
        superAdminOnly: true,
      },
    ],
  },
  {
    label: "Contenido",
    items: [
      {
        to: "/admin/portfolio",
        label: "Portfolio",
        icon: Image,
        end: false,
        hint: "Galería de fotos",
      },
      {
        to: "/admin/audiovisual",
        label: "Audiovisual",
        icon: Video,
        end: false,
        hint: "Videos y reels",
      },
      {
        to: "/admin/invitaciones",
        label: "Invitaciones",
        icon: Mail,
        end: false,
        hint: "Generador de invitaciones",
      },
    ],
  },
  {
    label: "Pagos",
    items: [
      {
        to: "/admin/revenue",
        label: "Revenue",
        icon: DollarSign,
        end: false,
        hint: "Ganancias, transacciones y top productos",
        revenueMetrics: true,
      },
      {
        to: "/admin/investor-kpi",
        label: "Investor KPI",
        icon: LineChart,
        end: false,
        hint: "North Star dual Stripe+MP, churn real P1–P5, MRR — scope juaperrone",
        superAdminOnly: true,
      },
      {
        to: "/admin/finanzas-personales",
        label: "Finanzas personales",
        icon: Wallet,
        end: false,
        hint: "Oruon en Postgres personal-finance (no DB negocio)",
        superAdminOnly: true,
      },
      {
        to: "/admin/stripe-disputes",
        label: "Disputas Stripe",
        icon: Scale,
        end: false,
        hint: "Gestión de disputas y contracargos",
      },
      {
        to: "/admin/stripe-payments-config",
        label: "Stripe · config",
        icon: Settings2,
        end: false,
        hint: "Adaptive Pricing y moneda de integración",
      },
    ],
  },
  {
    label: "Analítica",
    items: [
      {
        to: "/admin/analytics",
        label: "Analytics (hub)",
        icon: BarChart3,
        end: false,
        hint: "Dashboard · En vivo · Progreso mensual · Atribución · Reporte AR · Front Metrics · Mux",
      },
      {
        to: "/admin/analytics?tab=live",
        label: "En vivo",
        icon: Radio,
        end: false,
        hint: "Quién está entrando ahora en tiempo real: geo, source, landing, device, replay PostHog y detalle de red iplocate (operador/ISP + tipo de conexión: residencial/móvil/empresa/datacenter/VPN).",
      },
      {
        to: "/admin/analytics?tab=monthly",
        label: "Progreso mensual",
        icon: CalendarRange,
        end: false,
        hint: "Crecimiento semanal: eventos, intentos de pago, templates, mejoras/regresiones y detalle por landing/source",
      },
      {
        to: "/admin/analytics?tab=attribution",
        label: "Atribución (Growth)",
        icon: TrendingUp,
        end: false,
        hint: "De dónde vienen los visitors (referrer/UTM/search) y qué convierte. DB attribution_events + Slack realtime.",
      },
      {
        to: "/admin/seo-growth",
        label: "SEO Growth",
        icon: Sparkles,
        end: false,
        hint: "Backlinks + spam detection + 75 keyword opportunities (49 🇺🇸 US English como mercado principal, 25 🇦🇷 AR Spanish). Filtro por país. Research vía Claude MCP — sin token en runtime.",
      },
      {
        to: "/admin/analytics?tab=metrics",
        label: "Métricas Front",
        icon: LineChart,
        end: false,
        hint: "Países, vistas, tiempo, embudos CTA + video Mux",
        superAdminOnly: true,
      },
      {
        to: "/admin/ba-province-report",
        label: "Buenos Aires Report",
        icon: MapPinned,
        end: false,
        hint: "Reporte extenso de visitors de Buenos Aires (provincia + CABA) desde timestamp arbitrario",
        superAdminOnly: true,
      },
    ],
  },
  {
    label: "Geo & Edge",
    items: [
      {
        to: "/admin/country-control",
        label: "Country Control Center",
        icon: MapPinned,
        end: false,
        hint: "Hub principal geo: tabla de los 101 países con template + variante dual efectiva, mapa de dónde gestionar cada eje, DEFAULT global, búsqueda/filtros y edición inline. Punto de entrada antes de country-template / dual-cta / hero-cta.",
        superAdminOnly: true,
      },
      {
        to: "/admin/us-states",
        label: "US State Control",
        icon: MapPinned,
        end: false,
        hint: "50 estados + DC: template (scope state_us) y variante dual en un solo panel. Cascada city → state_us → country:US → DEFAULT. Bulk por región Census. Preview geo sim.",
        superAdminOnly: true,
      },
      {
        to: "/admin/cities",
        label: "City Control",
        icon: MapPinned,
        end: false,
        hint: "Toda ciudad real del tráfico (PostHog) con visitantes, idioma y lo que ve hoy: variante dual efectiva + template. Asigná variante/template por ciudad, sumá al catálogo (aliases del matcher) y simulá el preview. AR: dual sí, template lockeado.",
        superAdminOnly: true,
      },
      {
        to: "/admin/ar-city-map",
        label: "Mapa Ciudad → Provincia (AR)",
        icon: MapPin,
        end: false,
        hint: "Source of truth de cross-validación AMBA. Cambios en ~2s al edge.",
        superAdminOnly: true,
      },
      {
        to: "/admin/country-landings",
        label: "Landings SEO",
        icon: FlaskConical,
        end: false,
        // 2026-06: ahora es un editor COMPLETO de copy (migración 063). El override
        // DB pisa al config TS; en vivo en ≤10s, sin redeploy.
        hint: "CMS completo de las páginas SEO (/famosas-mexicanas, /argentinian-model…): title, H1, descripción, intro, bloques de contenido + CTA. En vivo, sin redeploy. No controla home ni BaseTemplate AR.",
        superAdminOnly: true,
      },
      {
        to: "/admin/worker-flags",
        label: "Worker · flags & idiomas",
        icon: Zap,
        end: false,
        // 2026-06: el panel YA tiene flags editables sin redeploy (kill switch, IG,
        // WhatsApp) + MULTILINGUAL_LANGS (habilitar pt/de/fr/it por mercado).
        hint: "Estado del Worker + flags editables sin redeploy (kill switch, IG CTA, WhatsApp) e idiomas del funnel intl (MULTILINGUAL_LANGS · pt/de/fr/it por mercado). Propaga al edge en ~1-2s.",
        superAdminOnly: true,
      },
    ],
  },
  {
    label: "Infraestructura",
    items: [
      {
        to: "/admin/config-health",
        label: "Salud de configuración",
        icon: HeartPulse,
        end: false,
        hint: "Variables, integraciones y checks del sistema",
        superAdminOnly: true,
      },
      {
        to: "/admin/audit-log",
        label: "Audit log",
        icon: History,
        end: false,
        hint: "Actividad admin: aprobaciones, bans, settings y sync del cron — quién, qué y cuándo",
        superAdminOnly: true,
      },
      {
        to: "/dashboard/env-setup",
        label: "Setup entorno",
        icon: ServerCog,
        end: false,
        hint: "Variables de entorno (Railway)",
        superAdminOnly: true,
      },
      {
        to: "/admin/sidebar-config",
        label: "Configuración del sidebar",
        icon: SlidersHorizontal,
        end: false,
        hint: "Ocultar módulos del menú para admins no super",
        superAdminOnly: true,
      },
    ],
  },
];

/** Flat list (backward-compatible export) — derived from the grouped structure. */
export const ADMIN_SIDEBAR_NAV_ITEMS: readonly AdminSidebarNavItem[] =
  ADMIN_SIDEBAR_NAV_GROUPS.flatMap((g) => g.items);

export function getAdminSidebarGroup(label: string): AdminSidebarNavGroup | undefined {
  return ADMIN_SIDEBAR_NAV_GROUPS.find((g) => g.label === label);
}

/** Atajos del dashboard = mismos ítems que el grupo Mux · Membresía del sidebar. */
export function getMuxMembershipDashboardItems(): readonly AdminSidebarNavItem[] {
  return getAdminSidebarGroup("Mux · Membresía")?.items ?? [];
}
